import { useEffect, useRef, useState } from "react";
import portalArt from "./assets/portal-concept.webp";
import loom from "./assets/weaving-loom.webp";
import playIcon from "./assets/play-fill.svg";
import pauseIcon from "./assets/pause-fill.svg";
import "./RajJourneyMotion.css";

const scenes = [
  {
    name: "سفارش",
    title: "هر فرش، از یک انتخاب شروع می‌شود.",
    description: "نماینده انتخابش را در پرتال ثبت می‌کند؛ خواستهٔ مشتری، نقطهٔ شروع کار است.",
    status: "انتخاب محصول و ثبت سفارش",
    image: portalArt,
  },
  {
    name: "طراحی",
    title: "ایده، آمادهٔ بافت می‌شود.",
    description: "طرح و رنگ‌بندی مرور می‌شوند تا نسخهٔ نهایی محصول مشخص باشد.",
    status: "بازبینی طرح و رنگ",
    image: "/raj/assets/design-proof.webp",
  },
  {
    name: "تولید",
    title: "نقش‌ها، جان می‌گیرند.",
    description: "برنامهٔ تولید به جریان کار تبدیل می‌شود؛ پیشرفت کار از نگاه مدیر دور نمی‌ماند.",
    status: "بافت فرش در جریان است",
    image: loom,
  },
  {
    name: "انبار محصول",
    title: "محصول آماده، موجودی روشن.",
    description: "فرش آماده در نمای موجودی دیده می‌شود تا تصمیم بعدی با آگاهی گرفته شود.",
    status: "محصول آماده در انبار",
    image: "/raj/assets/inventory-rugs.webp",
  },
];

/** Local visual storytelling only; it never drives a business workflow. */
export default function RajJourneyMotion() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [manuallySelected, setManuallySelected] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const running = playing && visible && pageVisible && !reducedMotion;

  useEffect(function () {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    function updatePreference() { setReducedMotion(media.matches); }
    function updateVisibility() { setPageVisible(!document.hidden); }
    updatePreference();
    updateVisibility();
    media.addEventListener("change", updatePreference);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = new IntersectionObserver(function (entries) {
      setVisible(entries[0]?.isIntersecting ?? false);
    }, { threshold: 0.25 });
    if (rootRef.current) observer.observe(rootRef.current);
    return function () {
      observer.disconnect();
      media.removeEventListener("change", updatePreference);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  useEffect(function () {
    if (!running) return;
    const timer = window.setTimeout(function () {
      setScene(function (current) { return (current + 1) % scenes.length; });
    }, 6200);
    return function () { window.clearTimeout(timer); };
  }, [scene, running]);

  const current = scenes[scene];

  return (
    <div
      className={`raj-journey ${running ? "is-running" : "is-paused"} ${reducedMotion ? "is-reduced" : ""} ${manuallySelected ? "is-manual" : ""}`}
      ref={rootRef}
      aria-label="روایت تصویری یک سفارش نمونه"
    >
      <div className="raj-journey__toolbar">
        <span>روایت یک سفارش نمونه</span>
        {!reducedMotion && (
          <button
            type="button"
            onClick={function () {
              setManuallySelected(false);
              setPlaying(!playing);
            }}
            aria-label={playing ? "توقف انیمیشن" : "پخش انیمیشن"}
          >
            <img
              src={playing ? pauseIcon : playIcon}
              alt=""
              width="18"
              height="18"
            />
            {playing ? "توقف" : "پخش"}
          </button>
        )}
      </div>
      <div className="raj-journey__scene">
        <div
          className={`raj-journey__visual raj-journey__visual--${scene}`}
          key={`art-${scene}`}
          aria-hidden="true"
        >
          <img
            className={scene === 2 ? "raj-journey__loom" : "raj-journey__art"}
            src={current.image}
            alt=""
            width="1100"
            height="800"
            loading="lazy"
          />
          {scene === 2 && (
            <img
              className="raj-journey__woven-carpet"
              src={loom}
              alt=""
              width="1100"
              height="800"
            />
          )}
        </div>
        <div
          className="raj-journey__copy"
          key={`copy-${scene}`}
        >
          <span className="raj-journey__status">{current.status}</span>
          <h3>{current.title}</h3>
          <p>{current.description}</p>
          <span className="raj-journey__counter">
            مرحلهٔ {new Intl.NumberFormat("fa").format(scene + 1)} از ۴
          </span>
        </div>
      </div>
      <ol className="raj-journey__steps">
        {scenes.map(function (item, index) {
          return (
            <li key={item.name}>
              <button
                type="button"
                aria-current={scene === index ? "step" : undefined}
                onClick={function () {
                  setScene(index);
                  setPlaying(false);
                  setManuallySelected(true);
                }}
              >
                <span>{new Intl.NumberFormat("fa").format(index + 1)}</span>
                {item.name}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
